import { useState } from "react";
import axios from "axios";
import { RotateCw, CheckCircle, XCircle, Settings, Calendar, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString("fr-FR");
};

const STATE_BADGES = {
  en_reconduction: { label: "🔁 En tacite reconduction", cls: "bg-purple-100 text-purple-800 border-purple-300" },
  reconduction_a_confirmer: { label: "⚠️ Reconduction à confirmer", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  resilie: { label: "Résilié", cls: "bg-gray-100 text-gray-700 border-gray-300" },
  expire: { label: "Expiré", cls: "bg-red-100 text-red-700 border-red-300" },
};

/**
 * Section "Tacite reconduction" à inclure dans la page ContratDetail.
 * Props: contract (l'objet contrat), onUpdate (callback après modification)
 */
export default function ClientContractRenewalSection({ contract, onUpdate }) {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showMarkForm, setShowMarkForm] = useState(false);

  const [tacite, setTacite] = useState(!!contract.tacite_reconduction);
  const [duree, setDuree] = useState(contract.duree_reconduction_mois || 12);
  const [preavis, setPreavis] = useState(contract.preavis_reconduction_jours || 90);
  const [newEndDate, setNewEndDate] = useState("");
  const [markDuree, setMarkDuree] = useState(contract.duree_reconduction_mois || 12);

  const state = contract.reconduction_state
    || (contract.en_reconduction ? "en_reconduction" : null);
  const stateBadge = state ? STATE_BADGES[state] : null;

  const history = contract.historique_reconductions || [];
  const nbRenewals = history.length;

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/contracts-renewals/${contract.id}/tacit-config`,
        {
          tacite_reconduction: tacite,
          duree_reconduction_mois: tacite ? Number(duree) : null,
          preavis_reconduction_jours: tacite ? Number(preavis) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configuration enregistrée");
      setShowConfig(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const markRenewed = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/contracts-renewals/${contract.id}/mark-renewed`,
        {
          new_date_fin: newEndDate || null,
          duree_reconduction_mois: Number(markDuree) || null,
          source: "manual",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reconduit — nouvelle échéance ${formatDate(res.data.new_date_fin)}`);
      setShowMarkForm(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const confirmProposedRenewal = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/contracts-renewals/${contract.id}/confirm-renewal?duree_mois=${duree}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Confirmé — nouvelle échéance ${formatDate(res.data.new_date_fin)}`);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const rejectProposedRenewal = async () => {
    if (!window.confirm("Rejeter la reconduction ? Le contrat sera marqué comme expiré.")) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/contracts-renewals/${contract.id}/reject-renewal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reconduction rejetée");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4" data-testid="client-renewal-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <RotateCw className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-800">Tacite reconduction</h3>
          {stateBadge && (
            <Badge className={stateBadge.cls}>{stateBadge.label}</Badge>
          )}
          {contract.tacite_reconduction && !state && (
            <Badge className="bg-blue-50 text-blue-700 border-blue-200">Surveillé</Badge>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfig(!showConfig)}
            data-testid="config-tacit-btn"
          >
            <Settings className="w-3.5 h-3.5 mr-1" /> Configurer
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMarkForm(!showMarkForm)}
            data-testid="mark-renewed-btn"
          >
            <Calendar className="w-3.5 h-3.5 mr-1" /> Marquer reconduit
          </Button>
        </div>
      </div>

      {/* Alert reconduction à confirmer */}
      {state === "reconduction_a_confirmer" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-wrap gap-2 items-center justify-between" data-testid="proposal-panel">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>Reconduction détectée automatiquement.</strong>
              <br />
              La date de fin ({formatDate(contract.date_fin)}) est dépassée. Confirmez ou rejetez la reconduction tacite.
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={confirmProposedRenewal}
              disabled={saving}
              data-testid="confirm-renewal-btn"
            >
              <CheckCircle className="w-4 h-4 mr-1" /> Confirmer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={rejectProposedRenewal}
              disabled={saving}
              data-testid="reject-renewal-btn"
            >
              <XCircle className="w-4 h-4 mr-1" /> Rejeter
            </Button>
          </div>
        </div>
      )}

      {/* Détails état actuel */}
      {state === "en_reconduction" && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm space-y-1">
          <div><span className="text-slate-500">Reconduit depuis :</span> <strong>{formatDate(contract.date_reconduction_actuelle)}</strong></div>
          <div><span className="text-slate-500">Nouvelle échéance :</span> <strong>{formatDate(contract.date_fin)}</strong></div>
          {contract.date_limite_preavis_reconduction && (
            <div><span className="text-slate-500">Préavis avant :</span> <strong>{formatDate(contract.date_limite_preavis_reconduction)}</strong></div>
          )}
          <div><span className="text-slate-500">Durée reconduction :</span> <strong>{contract.duree_reconduction_mois || "?"} mois</strong></div>
          {nbRenewals > 0 && (
            <div className="text-xs text-purple-600 pt-1">{nbRenewals} reconduction{nbRenewals > 1 ? "s" : ""} historique</div>
          )}
        </div>
      )}

      {/* Config form */}
      {showConfig && (
        <div className="bg-slate-50 rounded-lg p-3 space-y-3 border">
          <div className="flex items-center gap-2">
            <Switch
              checked={tacite}
              onCheckedChange={setTacite}
              data-testid="tacit-switch"
            />
            <Label className="text-sm">Ce contrat est à tacite reconduction</Label>
          </div>
          {tacite && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Durée de reconduction (mois)</Label>
                <Input
                  type="number" min={1} value={duree}
                  onChange={(e) => setDuree(e.target.value)}
                  data-testid="duree-input"
                />
              </div>
              <div>
                <Label className="text-xs">Préavis avant la prochaine échéance (jours)</Label>
                <Input
                  type="number" min={0} value={preavis}
                  onChange={(e) => setPreavis(e.target.value)}
                  data-testid="preavis-input"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowConfig(false)}>Annuler</Button>
            <Button size="sm" onClick={saveConfig} disabled={saving} data-testid="save-config-btn">
              {saving ? "..." : "Enregistrer"}
            </Button>
          </div>
        </div>
      )}

      {/* Mark renewed form */}
      {showMarkForm && (
        <div className="bg-slate-50 rounded-lg p-3 space-y-3 border">
          <p className="text-xs text-slate-600">
            Passer manuellement le contrat en reconduction. Si tu ne renseignes pas de nouvelle date de fin,
            elle sera calculée automatiquement (ancienne date + durée choisie).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nouvelle date de fin (optionnel)</Label>
              <Input
                type="date" value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                data-testid="new-end-date"
              />
            </div>
            <div>
              <Label className="text-xs">Durée de la reconduction (mois)</Label>
              <Input
                type="number" min={1} value={markDuree}
                onChange={(e) => setMarkDuree(e.target.value)}
                data-testid="mark-duree"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowMarkForm(false)}>Annuler</Button>
            <Button size="sm" onClick={markRenewed} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? "..." : "Marquer comme reconduit"}
            </Button>
          </div>
        </div>
      )}

      {/* Historique */}
      {history.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-2">Historique des reconductions</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {history.slice().reverse().map((h, i) => (
              <div key={h.id || i} className="text-xs bg-slate-50 rounded px-2 py-1.5 flex flex-wrap gap-2">
                <span className="text-slate-500">{formatDate(h.detected_at)}</span>
                <span>{formatDate(h.ancienne_date_fin)} → <strong>{formatDate(h.nouvelle_date_fin)}</strong></span>
                <span className="text-slate-500">({h.duree_mois} mois · {h.source})</span>
                {h.supplier_invoice_id && (
                  <span className="text-purple-600">📄 via facture leaseur</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
